import { ref } from 'vue';

// Color modes the app supports. These map 1:1 onto Bootstrap 5.3's
// `data-bs-theme` attribute values, which drive the whole --bs-* variable layer.
export const SUPPORTED_THEMES = ['light', 'dark'];
export const FALLBACK_THEME = 'light';

const STORAGE_KEY = 'theme';

// Browser/PWA chrome above the page. Kept in step with the app theme rather than
// the OS: a media-scoped <meta> pair would disagree with the screen now that the
// two are independent.
const THEME_COLORS = { light: '#ffffff', dark: '#14141F' };

/** Narrow any value to a supported theme, or null if unsupported. */
function normalizeTheme(code) {
    if (!code) return null;
    const short = String(code).toLowerCase();
    return SUPPORTED_THEMES.includes(short) ? short : null;
}

/**
 * Resolve the theme to boot with:
 *   1. explicitly stored `theme`
 *   2. fallback (light)
 *
 * The OS `prefers-color-scheme` setting is deliberately NOT consulted. Dark is
 * opt-in through the Profile switch, so the app looks the same on first run for
 * everyone, whatever their device is set to.
 *
 * Unlike the locale, this is a per-device display preference and is never
 * synced to the user record.
 */
function resolveInitialTheme() {
    return normalizeTheme(localStorage.getItem(STORAGE_KEY)) || FALLBACK_THEME;
}

const theme = ref(resolveInitialTheme());

/** The active theme code, for binding to third-party components. */
export function currentTheme() {
    return theme.value;
}

/** True while dark is active — handy for `:dark` style props. */
export function isDark() {
    return theme.value === 'dark';
}

/** Paint a theme without touching the stored preference. */
function applyTheme(code) {
    theme.value = code;
    document.documentElement.setAttribute('data-bs-theme', code);

    // Seeded in index.html so the first paint is already right; this keeps it
    // correct when the user toggles.
    const meta = document.querySelector('#theme-color-meta');
    if (meta) meta.setAttribute('content', THEME_COLORS[code]);
}

/** Switch the active theme and persist the choice. No-op if unsupported. */
export function setTheme(code) {
    const next = normalizeTheme(code);
    if (!next) return;
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
}

/** Flip between light and dark. */
export function toggleTheme() {
    setTheme(theme.value === 'dark' ? 'light' : 'dark');
}

// Keep <html data-bs-theme> in sync with the boot theme.
applyTheme(theme.value);

export default theme;
